import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Car, Copy, Plus, Search, UserPlus, Users } from 'lucide-react';
import { api } from '../../api';
import { NOME_PAPEL, pode, useSessao } from '../../sessao';
import { Modal } from '../../componentes/Modal';

export interface ItemUsuario {
  id: string; nome: string; email: string; telefone: string | null; papel: string; ativo: boolean; trocar_senha: boolean;
  ultimo_login_em: string | null; ajustes: number; sessoes: number; veiculo_placa: string | null; versao: number;
}
export interface Veiculo { id: string; placa: string; descricao: string | null; ativo: boolean; versao: number; tecnicos: { id: string; nome: string }[]; centro_estoque: string | null; centro_nome: string | null }

export const PAPEIS_EDITAVEIS = ['admin', 'despacho', 'tecnico', 'comercial', 'financeiro'];
export interface Troca {
  id: string; status: 'pendente' | 'confirmada' | 'descartada'; centro_externo: string; centro_nome: string | null; detectada_em: string; decidida_em: string | null;
  tecnicos_movidos: number | null; decidida_por: string | null; de: { id: string; placa: string; descricao: string | null }; para: { id: string; placa: string; descricao: string | null }; tecnicos: string[];
}
export const placaFmt = (p: string) => `${p.slice(0, 3)}-${p.slice(3)}`;
const quando = (iso: string | null) => iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : 'nunca entrou';

/** Mostra a senha provisória UMA vez, com botão de copiar. Depois de fechar, só gerando outra. */
export function SenhaProvisoria({ nome, senha, aoFechar }: { nome: string; senha: string; aoFechar: () => void }) {
  const [copiado, setCopiado] = useState(false);
  return (
    <div className="form">
      <p>Senha provisória de <strong>{nome}</strong>:</p>
      <div className="senha-provisoria">
        <code>{senha}</code>
        <button type="button" className="btn-fantasma" onClick={() => navigator.clipboard?.writeText(senha).then(() => setCopiado(true))}><Copy size={15} aria-hidden /> {copiado ? 'Copiada' : 'Copiar'}</button>
      </div>
      <p className="alerta alerta-aviso">Ela aparece só agora. Passe para a pessoa por um canal direto (pessoalmente ou mensagem privada). No primeiro acesso o sistema obriga a criar uma senha própria.</p>
      <div className="acoes-form"><button className="btn" onClick={aoFechar}>Pronto</button></div>
    </div>
  );
}

function FormNovoUsuario({ aoCriar, aoCancelar }: { aoCriar: (id: string, nome: string, senha: string) => void; aoCancelar: () => void }) {
  const [f, setF] = useState({ nome: '', email: '', telefone: '', papel: 'tecnico' });
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  async function enviar(e: FormEvent) {
    e.preventDefault(); setErro(null); setSalvando(true);
    try {
      const r = await api<{ id: string; senha_provisoria: string }>('/usuarios', { metodo: 'POST', corpo: { ...f, telefone: f.telefone || null } });
      aoCriar(r.id, f.nome, r.senha_provisoria);
    } catch (err) { setErro(err instanceof Error ? err.message : 'Não foi possível criar.'); } finally { setSalvando(false); }
  }
  return (
    <form className="form" onSubmit={enviar}>
      <label>Nome<input required minLength={2} maxLength={120} value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} autoFocus /></label>
      <label>E-mail (é o login)<input type="email" required maxLength={200} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></label>
      <div className="linha-2">
        <label>Papel
          <select value={f.papel} onChange={(e) => setF({ ...f, papel: e.target.value })}>{PAPEIS_EDITAVEIS.map((p) => <option key={p} value={p}>{NOME_PAPEL[p]}</option>)}</select>
        </label>
        <label>Telefone (opcional)<input inputMode="tel" maxLength={30} value={f.telefone} onChange={(e) => setF({ ...f, telefone: e.target.value })} placeholder="(19) 99999-9999" /></label>
      </div>
      <small className="ajuda">O papel traz as permissões padrão. Exceções (dar ou tirar uma permissão só desta pessoa) ficam na ficha do usuário.</small>
      {erro && <p className="alerta" role="alert">{erro}</p>}
      <div className="acoes-form">
        <button type="button" className="btn-fantasma" onClick={aoCancelar}>Cancelar</button>
        <button className="btn" disabled={salvando}>{salvando ? 'Criando…' : 'Criar e gerar senha'}</button>
      </div>
    </form>
  );
}

function Usuarios() {
  const { usuario } = useSessao();
  const navegar = useNavigate();
  const [params, setParams] = useSearchParams();
  const [busca, setBusca] = useState(params.get('busca') ?? '');
  const [lista, setLista] = useState<ItemUsuario[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [novo, setNovo] = useState(false);
  const [senha, setSenha] = useState<{ id: string; nome: string; senha: string } | null>(null);
  const situacao = params.get('situacao') ?? 'ativos';
  const gerir = pode(usuario, 'usuarios.gerir');

  useEffect(() => {
    const q = new URLSearchParams({ situacao });
    if (params.get('busca')) q.set('busca', params.get('busca')!);
    api<ItemUsuario[]>(`/usuarios?${q}`).then(setLista).catch((e) => setErro(e.message));
  }, [params, situacao]);
  useEffect(() => {
    const t = setTimeout(() => {
      if ((params.get('busca') ?? '') === busca) return;
      const p = new URLSearchParams(params); busca ? p.set('busca', busca) : p.delete('busca'); setParams(p, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [busca]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className="barra-filtros">
        <label className="campo-busca">
          <Search size={18} strokeWidth={1.7} aria-hidden />
          <input type="search" placeholder="Nome ou e-mail" value={busca} onChange={(e) => setBusca(e.target.value)} aria-label="Buscar usuários" />
        </label>
        <div className="segmentos" role="group" aria-label="Situação">
          {[['ativos', 'Ativos'], ['inativos', 'Desativados'], ['todos', 'Todos']].map(([v, r]) => (
            <button key={v} className={situacao === v ? 'ativo' : ''} onClick={() => { const p = new URLSearchParams(params); p.set('situacao', v!); setParams(p); }}>{r}</button>
          ))}
        </div>
        {gerir && <button className="btn" onClick={() => setNovo(true)}><UserPlus size={18} aria-hidden /> Novo usuário</button>}
      </div>
      {erro && <p className="alerta" role="alert">{erro}</p>}
      {lista && lista.length === 0 && <div className="cartao vazio"><div className="icone-tile"><Users size={24} strokeWidth={1.7} aria-hidden /></div><p>Nenhum usuário com esse filtro.</p></div>}
      {lista && lista.length > 0 && (
        <div className="cartao tabela-cartao rolagem-x">
          <table className="tabela tabela-usuarios">
            <thead><tr><th>Nome</th><th>Papel</th><th>Carro</th><th>Último acesso</th><th>Situação</th></tr></thead>
            <tbody>
              {lista.map((u) => (
                <tr key={u.id} className={u.ativo ? '' : 'inativo'} onClick={() => navegar(`/equipe/usuarios/${u.id}`)}>
                  <td><Link to={`/equipe/usuarios/${u.id}`} onClick={(e) => e.stopPropagation()}><strong>{u.nome}</strong></Link><small>{u.email}</small></td>
                  <td>{NOME_PAPEL[u.papel] ?? u.papel}{u.ajustes > 0 && <small className="selo-ajuste">{u.ajustes} exceç{u.ajustes === 1 ? 'ão' : 'ões'} de permissão</small>}</td>
                  <td className="mono">{u.veiculo_placa ? placaFmt(u.veiculo_placa) : '—'}</td>
                  <td>{quando(u.ultimo_login_em)}{u.sessoes > 0 && <small>{u.sessoes} sessão(ões) aberta(s)</small>}</td>
                  <td>{!u.ativo ? <span className="situacao s-na">Desativado</span> : u.trocar_senha ? <span className="situacao s-aviso">Senha provisória</span> : <span className="situacao s-ok">Ativo</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Modal titulo="Novo usuário" aberto={novo} aoFechar={() => setNovo(false)}>
        <FormNovoUsuario aoCancelar={() => setNovo(false)} aoCriar={(id, nome, s) => { setNovo(false); setSenha({ id, nome, senha: s }); }} />
      </Modal>
      <Modal titulo="Usuário criado" aberto={Boolean(senha)} aoFechar={() => { const id = senha?.id; setSenha(null); if (id) navegar(`/equipe/usuarios/${id}`); }}>
        {senha && <SenhaProvisoria nome={senha.nome} senha={senha.senha} aoFechar={() => { const id = senha.id; setSenha(null); navegar(`/equipe/usuarios/${id}`); }} />}
      </Modal>
    </>
  );
}

function Veiculos() {
  const { usuario } = useSessao();
  const gerir = pode(usuario, 'veiculos.gerir');
  const [lista, setLista] = useState<Veiculo[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [novo, setNovo] = useState(false);
  const [f, setF] = useState({ placa: '', descricao: '' });
  const [trocas, setTrocas] = useState<Troca[]>([]);
  const carregar = useCallback(() => {
    api<Veiculo[]>('/veiculos').then(setLista).catch((e) => setErro(e.message));
    api<Troca[]>('/veiculos/trocas').then(setTrocas).catch(() => setTrocas([]));
  }, []);
  useEffect(carregar, [carregar]);

  async function decidirTroca(t: Troca, confirmar: boolean) {
    const txt = confirmar
      ? `Confirmar a troca ${placaFmt(t.de.placa)} → ${placaFmt(t.para.placa)}?\n\n${t.tecnicos.length ? `${t.tecnicos.join(', ')} passa(m) a usar o carro novo` : 'Nenhum técnico usa o carro antigo'} e ${placaFmt(t.de.placa)} é desativado.`
      : `Descartar? Use quando a placa foi digitada errada no EstoqueMaza (corrija lá também).`;
    if (!window.confirm(txt)) return;
    setErro(null);
    try { await api(`/veiculos/trocas/${t.id}/${confirmar ? 'confirmar' : 'descartar'}`, { metodo: 'POST', corpo: {} }); carregar(); }
    catch (err) { setErro(err instanceof Error ? err.message : 'Não foi possível.'); }
  }
  const pendentes = trocas.filter((t) => t.status === 'pendente');
  const recentes = trocas.filter((t) => t.status !== 'pendente').slice(0, 5);

  async function criar(e: FormEvent) {
    e.preventDefault(); setErro(null);
    try { await api('/veiculos', { metodo: 'POST', corpo: { placa: f.placa, descricao: f.descricao || null } }); setNovo(false); setF({ placa: '', descricao: '' }); carregar(); }
    catch (err) { setErro(err instanceof Error ? err.message : 'Não foi possível cadastrar.'); }
  }
  async function alternar(v: Veiculo) {
    if (v.ativo && v.tecnicos.length && !window.confirm(`Desativar ${placaFmt(v.placa)}? ${v.tecnicos.map((t) => t.nome).join(', ')} deixa(m) de ter carro habitual.`)) return;
    try { await api(`/veiculos/${v.id}`, { metodo: 'PATCH', corpo: { ativo: !v.ativo, versao: v.versao } }); carregar(); } catch (err) { setErro((err as Error).message); }
  }

  return (
    <>
      <div className="barra-filtros">
        <p className="nota">A placa liga o carro ao centro dele no EstoqueMaza (baixa das peças). Trocou de carro: mude a placa do centro no EstoqueMaza e cadastre a placa nova aqui — a sincronização detecta a troca e um admin confirma abaixo.</p>
        {gerir && <button className="btn" onClick={() => setNovo(true)}><Plus size={18} aria-hidden /> Novo veículo</button>}
      </div>
      {erro && <p className="alerta" role="alert">{erro}</p>}
      {pendentes.map((t) => (
        <div key={t.id} className="cartao aviso-troca" role="status">
          <div>
            <strong>Troca de carro detectada — {t.centro_nome ?? `centro ${t.centro_externo}`}</strong>
            <p className="nota">
              <span className="mono">{placaFmt(t.de.placa)}</span>{t.de.descricao ? ` (${t.de.descricao})` : ''} → <span className="mono">{placaFmt(t.para.placa)}</span>{t.para.descricao ? ` (${t.para.descricao})` : ''}
              {' · '}{t.tecnicos.length ? `técnico(s): ${t.tecnicos.join(', ')}` : 'nenhum técnico no carro antigo'}
              {' · '}detectada em {new Date(t.detectada_em).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
            </p>
            {!gerir && <p className="nota">Só um administrador pode confirmar.</p>}
          </div>
          {gerir && <div className="acoes-form">
            <button className="btn-fantasma" onClick={() => decidirTroca(t, false)}>Descartar</button>
            <button className="btn" onClick={() => decidirTroca(t, true)}>Confirmar troca</button>
          </div>}
        </div>
      ))}
      {lista && lista.length === 0 && <div className="cartao vazio"><div className="icone-tile"><Car size={24} strokeWidth={1.7} aria-hidden /></div><p>Nenhum veículo cadastrado.</p></div>}
      {lista && lista.length > 0 && (
        <div className="cartao tabela-cartao rolagem-x">
          <table className="tabela">
            <thead><tr><th>Placa</th><th>Técnicos (carro habitual)</th><th>Centro no EstoqueMaza</th><th>Situação</th>{gerir && <th aria-label="Ações" />}</tr></thead>
            <tbody>
              {lista.map((v) => (
                <tr key={v.id} className={v.ativo ? '' : 'inativo'}>
                  <td><strong className="mono">{placaFmt(v.placa)}</strong>{v.descricao && <small>{v.descricao}</small>}</td>
                  <td>{v.tecnicos.length ? v.tecnicos.map((t) => <Link key={t.id} to={`/equipe/usuarios/${t.id}`} className="bloco">{t.nome}</Link>) : '—'}</td>
                  <td>{v.centro_estoque ? <span className="situacao s-ok">{v.centro_nome ?? `centro ${v.centro_estoque}`}</span> : <span className="situacao s-na">Sem centro</span>}</td>
                  <td>{v.ativo ? 'Em uso' : 'Desativado'}</td>
                  {gerir && <td className="acoes"><button className="btn-fantasma" onClick={() => alternar(v)}>{v.ativo ? 'Desativar' : 'Reativar'}</button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {recentes.length > 0 && (
        <details className="cartao" style={{ marginTop: 16 }}>
          <summary>Trocas decididas nos últimos 30 dias ({recentes.length})</summary>
          <ul className="lista-simples">
            {recentes.map((t) => <li key={t.id}><span className="mono">{placaFmt(t.de.placa)} → {placaFmt(t.para.placa)}</span> · {t.status === 'confirmada' ? `confirmada (${t.tecnicos_movidos ?? 0} técnico(s) movido(s))` : 'descartada'} por {t.decidida_por ?? '—'} em {t.decidida_em ? new Date(t.decidida_em).toLocaleDateString('pt-BR') : '—'}</li>)}
          </ul>
        </details>
      )}
      <Modal titulo="Novo veículo" aberto={novo} aoFechar={() => setNovo(false)}>
        <form className="form" onSubmit={criar}>
          <label>Placa<input required maxLength={8} value={f.placa} onChange={(e) => setF({ ...f, placa: e.target.value.toUpperCase() })} placeholder="ABC1D23" autoFocus /></label>
          <label>Descrição (opcional)<input maxLength={120} value={f.descricao} onChange={(e) => setF({ ...f, descricao: e.target.value })} placeholder="Strada branca" /></label>
          <div className="acoes-form">
            <button type="button" className="btn-fantasma" onClick={() => setNovo(false)}>Cancelar</button>
            <button className="btn" disabled={f.placa.replace(/[^A-Z0-9]/gi, '').length !== 7}>Cadastrar</button>
          </div>
        </form>
      </Modal>
    </>
  );
}

export function Equipe() {
  const [params, setParams] = useSearchParams();
  const aba = params.get('aba') === 'veiculos' ? 'veiculos' : 'usuarios';
  return (
    <>
      <div className="cabecalho-pagina">
        <div>
          <span className="kicker">Administração</span>
          <h1 className="titulo">Equipe</h1>
        </div>
        <div className="segmentos" role="tablist" aria-label="Seção">
          <button role="tab" aria-selected={aba === 'usuarios'} className={aba === 'usuarios' ? 'ativo' : ''} onClick={() => setParams({})}><Users size={15} aria-hidden /> Usuários</button>
          <button role="tab" aria-selected={aba === 'veiculos'} className={aba === 'veiculos' ? 'ativo' : ''} onClick={() => setParams({ aba: 'veiculos' })}><Car size={15} aria-hidden /> Veículos</button>
        </div>
      </div>
      {aba === 'usuarios' ? <Usuarios /> : <Veiculos />}
    </>
  );
}
